'use client';

import { useMutation } from '@tanstack/react-query';
import confetti from 'canvas-confetti';
import { BackgroundBeams } from 'components/bg-beams/bg-beams';
import { IconLoading02 } from 'icon/General/loading-02';
import { useTranslations } from 'next-intl';
import { api } from 'orpc/react';
import {
  Button,
  FocusableLink,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
  toast,
  useForm,
  zodResolver,
} from 'ui';
import { z } from 'zod';

export type NewsletterFormProps = {
  siteId: string;
  siteName: string;
  title?: string | null;
  description?: string | null;
  className?: string;
};

export function NewsletterForm({
  siteId,
  siteName,
  title,
  description,
  className,
}: NewsletterFormProps) {
  const t = useTranslations('Blog.Newsletter');
  return (
    <section className={className}>
      <div className="newsletter-panel">
        <div className="newsletter-copy">
          <h2 className="newsletter-title">
            {title ||
              t.rich('Title', {
                siteName: () => (
                  <span className="newsletter-site-name">
                    {siteName.replace(/_/g, ' ')}
                  </span>
                ),
              })}
          </h2>
          <p className="newsletter-description">
            {description || t('Description')}
          </p>
        </div>
        <div className="newsletter-form-wrapper">
          <SubscribeForm siteId={siteId} size="lg" />
        </div>
        <BackgroundBeams className="newsletter-background" />
      </div>
    </section>
  );
}

type SubscribeFormProps = {
  size?: 'default' | 'lg';
  siteId: string;
};

function SubscribeForm({
  size = 'default',
  siteId,
}: SubscribeFormProps): JSX.Element {
  const t = useTranslations('Blog.Newsletter');
  const subscribeSchema = z.object({
    email: z.string().email(t('InvalidEmail')),
  });
  type SubscribeFormValues = z.infer<typeof subscribeSchema>;
  const form = useForm<SubscribeFormValues>({
    resolver: zodResolver(subscribeSchema),
    defaultValues: {
      email: '',
    },
  });
  const { mutate, isPending } = useMutation(
    api.blog.subscribe.mutationOptions({
      onSuccess: (data) => {
        if ('error' in data) {
          if (data.error === 'duplicated') {
            toast.success(t('AlreadySubscribed'));
            void confetti({
              particleCount: 100,
              spread: 70,
              origin: { y: 0.6 },
            });
            form.reset();
          } else {
            toast.error(t('Error'));
          }
          return;
        }
        toast.success(t('Success'));
        void confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
        });
        form.reset();
      },
      onError: (error) => {
        toast.error(error.message || t('Error'));
      },
    }),
  );

  function onSubmit(data: SubscribeFormValues) {
    mutate({
      email: data.email,
      siteId,
    });
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="newsletter-subscribe-form"
      >
        <div className="newsletter-field">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className="newsletter-email-input"
                    placeholder="Email"
                    inputSize={size}
                    {...field}
                  />
                </FormControl>
                <FormMessage className="newsletter-error" />
              </FormItem>
            )}
          />
          <p className="newsletter-privacy">
            {t.rich('PrivacyPolicy', {
              link: (chunks) => (
                <FocusableLink
                  className="newsletter-privacy-link"
                  href="https://quotion.co/privacy-policy"
                  target="_blank"
                >
                  {chunks}
                </FocusableLink>
              ),
            })}
          </p>
        </div>
        <Button
          size={size}
          type="submit"
          className="newsletter-submit"
          disabled={isPending}
        >
          {isPending && (
            <IconLoading02
              className="newsletter-loading"
              height={20}
              width={20}
            />
          )}
          {t('Button')}
        </Button>
      </form>
    </Form>
  );
}
